import { useCallback } from 'react';
import axios from 'axios';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  createBook,
  updateBook,
  fetchBook,
  updateBookStep,
  submitBookForReview,
  validateAndSetStep,
  clearError,
  BookData
} from '../store/slices/bookSlice';
import { endpoints } from '../api/api';
import { trackBookCreated, trackBookSubmitted } from '../utils/analytics';

const replaceId = (url: string, id: string) => url.replace(':id', id);

export const useBookAPI = () => {
  const dispatch = useAppDispatch();
  const {
    bookId,
    bookData,
    isSaving,
    isLoading,
    error,
    currentStep
  } = useAppSelector((state) => state.book);

  // Helper to filter out file fields that should only be managed by file upload endpoints
  const filterFileFields = (data: Partial<BookData>): Partial<BookData> => {
    const filtered = { ...data };
    // Remove uploadedFile if it's null or a string (managed by file upload endpoints)
    if (filtered.uploadedFile === null || typeof filtered.uploadedFile === 'string') {
      delete filtered.uploadedFile;
    }
    // Remove coverImage if it's null or a string (managed by file upload endpoints)
    if (filtered.coverImage === null || typeof filtered.coverImage === 'string') {
      delete filtered.coverImage;
    }
    // Same as coverImage above — set only by the file upload endpoint. Sending
    // an explicit null here (Redux's initial-state default) fails the backend's
    // enum validator, which allows the string values but not null.
    if (filtered.coverImageSource === null || typeof filtered.coverImageSource === 'string') {
      delete filtered.coverImageSource;
    }
    // Don't send empty content — PDF-upload users have no editor content, so sending
    // empty sections would trigger the blank-wipe guard and block their save/submit.
    // When content is absent from the payload the backend leaves DB content untouched.
    if (filtered.content !== undefined) {
      const hasHtml = !!filtered.content?.html?.trim();
      const hasSections = (filtered.content?.json?.sections as any[])?.some(
        (s: any) => s?.content?.trim()
      );
      if (!hasHtml && !hasSections) delete (filtered as any).content;
    }
    return filtered;
  };

  // Save current book data - creates if no bookId, updates if bookId exists
  const saveBook = useCallback(async (additionalData?: Partial<BookData>) => {
    const dataToSave = filterFileFields({ ...bookData, ...additionalData });

    if (bookId) {
      // Update existing book
      return dispatch(updateBook({ bookId, bookData: dataToSave }));
    } else {
      // Create new book
      return dispatch(createBook(dataToSave));
    }
  }, [bookId, bookData, dispatch]);

  // Save and advance to next step
  const saveAndContinue = useCallback(async (
    nextStep: string,
    additionalData?: Partial<BookData>
  ) => {
    const dataToSave = filterFileFields({ ...bookData, ...additionalData });

    let result;
    let newBookId = bookId;

    if (bookId) {
      // Update existing book
      result = await dispatch(updateBook({ bookId, bookData: dataToSave }));
    } else {
      // Create new book
      result = await dispatch(createBook(dataToSave));
      if (result.meta.requestStatus === 'fulfilled') {
        const createdBook = (result.payload as any).book;
        newBookId = createdBook._id;
        trackBookCreated(createdBook._id, createdBook.title || 'Untitled');
      }
    }

    // If save was successful, update the step on backend. Return the step-update
    // result (not the save result) when it's attempted — otherwise a caller
    // checking the return value would see the save's "fulfilled" status even when
    // the step transition itself was rejected (e.g. the approval gate), and treat
    // the whole operation as having silently succeeded.
    if (result.meta.requestStatus === 'fulfilled' && newBookId) {
      const stepResult = await dispatch(updateBookStep({ bookId: newBookId, currentStep: nextStep }));
      if (stepResult.meta.requestStatus === 'fulfilled') {
        dispatch(validateAndSetStep(nextStep));
      }
      return stepResult;
    }

    return result;
  }, [bookId, bookData, dispatch]);

  // Load book for editing
  const loadBook = useCallback(async (id: string) => {
    return dispatch(fetchBook(id));
  }, [dispatch]);

  // Submit book for review
  const submitForReview = useCallback(async (extraTrackingProps?: Record<string, any>) => {
    if (!bookId) {
      return { meta: { requestStatus: 'rejected' }, error: 'No book ID' };
    }

    // First save any pending changes (filter out file fields)
    const dataToSave = filterFileFields(bookData);
    const saveResult = await dispatch(updateBook({ bookId, bookData: dataToSave }));

    if (saveResult.meta.requestStatus === 'fulfilled') {
      const submitResult = await dispatch(submitBookForReview(bookId));
      if (submitResult.meta.requestStatus === 'fulfilled') {
        trackBookSubmitted(bookId, bookData.title || 'Untitled', extraTrackingProps);
      }
      return submitResult;
    }

    return saveResult;
  }, [bookId, bookData, dispatch]);

  const startVerification = useCallback(async (id: string) => {
    try {
      const response = await axios.post(replaceId(endpoints.verifyBook, id));
      return response.data;
    } catch (err: any) {
      return err?.data || { success: false, message: 'Failed to start verification' };
    }
  }, []);

  const getVerifyStatus = useCallback(async (id: string) => {
    try {
      const response = await axios.get(replaceId(endpoints.getVerifyStatus, id));
      return response.data;
    } catch (err: any) {
      return null;
    }
  }, []);

  const startPdfImport = useCallback(async (id: string) => {
    try {
      const response = await axios.post(replaceId(endpoints.initiatePdfImport, id));
      return response.data;
    } catch (err: any) {
      return err?.data || { success: false, message: 'Failed to start PDF import' };
    }
  }, []);

  const getPdfImportStatus = useCallback(async (id: string) => {
    try {
      const response = await axios.get(replaceId(endpoints.getPdfImportStatus, id));
      return response.data;
    } catch (err: any) {
      return null;
    }
  }, []);

  const resizePdf = useCallback(async (id: string) => {
    try {
      const response = await axios.post(replaceId(endpoints.resizePdf, id));
      return response.data;
    } catch (err: any) {
      return err?.response?.data || { success: false, message: 'Failed to resize PDF' };
    }
  }, []);

  // Clear any error
  const clearBookError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  return {
    saveBook,
    saveAndContinue,
    loadBook,
    submitForReview,
    startVerification,
    getVerifyStatus,
    startPdfImport,
    getPdfImportStatus,
    resizePdf,
    clearBookError,
    bookId,
    bookData,
    isSaving,
    isLoading,
    error,
    currentStep
  };
};
